import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  OverviewTotalsResponse,
  ProgrammeGroupSummary,
} from "../../lib/api/overview";
import { formatMoney } from "../../lib/format/money";
import { groupColor } from "../../lib/group-colors";
import { Chip, ChipGroup } from "../../components/ui/Chip";
import { EmptyState } from "../../components/ui/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/ui/Table";

type ProgrammeFinancialRow = OverviewTotalsResponse["overview"]["programmes"][number];

interface ProgrammeFinancialsSectionProps {
  groups: ProgrammeGroupSummary[];
  financialRows: ProgrammeFinancialRow[];
  period: string;
  scopeLabel: string;
  onUnitSelect?: (unit: ProgrammeGroupSummary["units"][number]) => void;
}

function periodLabel(period: string): string {
  const date = new Date(`${period}-01T12:00:00`);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function sumAmounts(values: string[]): string {
  const total = values.reduce((sum, value) => sum + Number.parseFloat(value || "0"), 0);
  return total.toFixed(2);
}

function budgetUsedPct(ytdPaid: string, annualBudget: string): number {
  const budget = Number.parseFloat(annualBudget);
  if (budget <= 0) {
    return 0;
  }
  return Math.min(100, (Number.parseFloat(ytdPaid) / budget) * 100);
}

function netTone(net: string): string {
  return Number.parseFloat(net) >= 0 ? "text-edlab-green" : "text-amber-600";
}

function groupChipLabel(group: ProgrammeGroupSummary): string {
  return group.code === "dlp" ? "DLP" : group.name;
}

interface AggregatedRow {
  annualBudget: string;
  periodIncome: string;
  periodPaid: string;
  periodNet: string;
  ytdIncome: string;
  ytdPaid: string;
  ytdNet: string;
}

function aggregateRows(rows: ProgrammeFinancialRow[]): AggregatedRow {
  return {
    annualBudget: sumAmounts(rows.map((row) => row.annualBudget)),
    periodIncome: sumAmounts(rows.map((row) => row.periodIncome)),
    periodPaid: sumAmounts(rows.map((row) => row.periodPaid)),
    periodNet: sumAmounts(rows.map((row) => row.periodNet)),
    ytdIncome: sumAmounts(rows.map((row) => row.ytdIncome)),
    ytdPaid: sumAmounts(rows.map((row) => row.ytdPaid)),
    ytdNet: sumAmounts(rows.map((row) => row.ytdNet)),
  };
}

function FinancialCells({ row }: { row: AggregatedRow }) {
  const usedPct = budgetUsedPct(row.ytdPaid, row.annualBudget);

  return (
    <>
      <TableCell className="text-right tabular-nums">{formatMoney(row.annualBudget)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMoney(row.periodIncome)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMoney(row.periodPaid)}</TableCell>
      <TableCell className={`text-right tabular-nums ${netTone(row.periodNet)}`}>
        {formatMoney(row.periodNet)}
      </TableCell>
      <TableCell className="text-right tabular-nums">{formatMoney(row.ytdIncome)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatMoney(row.ytdPaid)}</TableCell>
      <TableCell className={`text-right tabular-nums ${netTone(row.ytdNet)}`}>
        {formatMoney(row.ytdNet)}
      </TableCell>
      <TableCell>
        <div className="flex min-w-[88px] items-center gap-2">
          <div className="h-[5px] flex-1 overflow-hidden rounded-lg bg-[#edf1f3]">
            <div
              className="h-full rounded-lg bg-[#2b9177]"
              style={{ width: `${usedPct}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-edlab-muted">{usedPct.toFixed(0)}%</span>
        </div>
      </TableCell>
    </>
  );
}

export function ProgrammeFinancialsSection({
  groups,
  financialRows,
  period,
  scopeLabel,
  onUnitSelect,
}: ProgrammeFinancialsSectionProps) {
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  useEffect(() => {
    if (expandedGroups.length === 0 && groups.length > 0) {
      const schools = groups.find((group) => group.code === "schools");
      if (schools) {
        setExpandedGroups([schools.id]);
      }
    }
  }, [expandedGroups.length, groups]);

  const financialByUnitId = useMemo(
    () => new Map(financialRows.map((row) => [row.unitId, row])),
    [financialRows],
  );

  const visibleGroups = useMemo(() => {
    if (groupFilter === "all") {
      return groups;
    }
    return groups.filter((group) => group.id === groupFilter);
  }, [groupFilter, groups]);

  const unitCount = visibleGroups.reduce((count, group) => count + group.units.length, 0);

  function toggleGroup(groupId: string): void {
    setExpandedGroups((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId],
    );
  }

  function isExpanded(groupId: string): boolean {
    return groupFilter !== "all" || expandedGroups.includes(groupId);
  }

  if (!groups.length) {
    return (
      <EmptyState
        title="No programmes in scope"
        description="Your account does not currently have access to any programme units."
      />
    );
  }

  return (
    <div className="space-y-4">
      <ChipGroup>
        <Chip
          active={groupFilter === "all"}
          onClick={() => {
            setGroupFilter("all");
          }}
        >
          All programmes
        </Chip>
        {groups.map((group) => (
          <Chip
            key={group.id}
            active={groupFilter === group.id}
          onClick={() => {
            setGroupFilter(group.id);
            setExpandedGroups((current) =>
              current.includes(group.id) ? current : [...current, group.id],
            );
          }}
          >
            {groupChipLabel(group)}
          </Chip>
        ))}
      </ChipGroup>

      <Table caption="Programme financials">
        <TableHead>
          <TableRow>
            <TableHeaderCell>Programme / unit</TableHeaderCell>
            <TableHeaderCell className="text-right">Annual budget</TableHeaderCell>
            <TableHeaderCell className="text-right">Month income</TableHeaderCell>
            <TableHeaderCell className="text-right">Month paid</TableHeaderCell>
            <TableHeaderCell className="text-right">Month net</TableHeaderCell>
            <TableHeaderCell className="text-right">YTD income</TableHeaderCell>
            <TableHeaderCell className="text-right">YTD paid</TableHeaderCell>
            <TableHeaderCell className="text-right">YTD net</TableHeaderCell>
            <TableHeaderCell>Budget used</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleGroups.map((group) => {
            const unitRows = group.units
              .map((unit) => financialByUnitId.get(unit.id))
              .filter((row): row is ProgrammeFinancialRow => row !== undefined);
            const groupTotals = aggregateRows(
              unitRows.length
                ? unitRows
                : group.units.map((unit) => ({
                    unitId: unit.id,
                    unitName: unit.name,
                    groupName: group.name,
                    annualBudget: unit.annualBudget,
                    periodIncome: "0.00",
                    periodPaid: "0.00",
                    periodNet: "0.00",
                    ytdIncome: unit.ytdIncome ?? "0.00",
                    ytdPaid: unit.ytdPaid ?? "0.00",
                    ytdNet: unit.ytdNet ?? "0.00",
                  })),
            );
            const expanded = isExpanded(group.id);

            return (
              <Fragment key={group.id}>
                <TableRow>
                  <TableCell>
                    <button
                      type="button"
                      className="flex w-full items-start gap-3 text-left"
                      onClick={() => {
                        toggleGroup(group.id);
                      }}
                    >
                      <span
                        className="mt-1 h-3 w-3 shrink-0 rounded-full"
                        style={{ backgroundColor: groupColor(group.code) }}
                      />
                      <span>
                        <span className="block font-semibold text-edlab-ink">{group.name}</span>
                        <span className="text-xs text-edlab-muted">
                          {group.units.length}{" "}
                          {group.units.length === 1 ? "unit" : "units"} ·{" "}
                          {expanded ? "Collapse" : "Expand"}
                        </span>
                      </span>
                    </button>
                  </TableCell>
                  <FinancialCells row={groupTotals} />
                </TableRow>
                {expanded
                  ? group.units.map((unit) => {
                      const row = financialByUnitId.get(unit.id) ?? {
                        unitId: unit.id,
                        unitName: unit.name,
                        groupName: group.name,
                        annualBudget: unit.annualBudget,
                        periodIncome: "0.00",
                        periodPaid: "0.00",
                        periodNet: "0.00",
                        ytdIncome: unit.ytdIncome ?? "0.00",
                        ytdPaid: unit.ytdPaid ?? "0.00",
                        ytdNet: unit.ytdNet ?? "0.00",
                      };

                      return (
                        <TableRow key={unit.id}>
                          <TableCell className="pl-10">
                            {onUnitSelect ? (
                              <button
                                type="button"
                                className="font-medium text-edlab-green hover:underline"
                                onClick={() => {
                                  onUnitSelect(unit);
                                }}
                              >
                                {unit.name}
                              </button>
                            ) : (
                              <span className="font-medium text-edlab-ink">{unit.name}</span>
                            )}
                          </TableCell>
                          <FinancialCells row={row} />
                        </TableRow>
                      );
                    })
                  : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-edlab-muted">
        <span>
          {unitCount} {unitCount === 1 ? "unit" : "units"} · {scopeLabel}
        </span>
        <span>
          {periodLabel(period)} and financial year to date · PKR · Paid basis
        </span>
      </div>
    </div>
  );
}
